import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  BtnGhost,
  BtnPrimary,
  Field,
  Select,
  TextArea,
  TextInput,
} from '@/components/field'
import { useToast } from '@/components/toast'
import { api, apiMutate, type ProductDetail } from '@/lib/api'
import { centsFromDollars } from '@/lib/labels'

type CategoryOpt = { id: string; slug: string; name: string }

export function ProduitNouveauPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [sku, setSku] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [priceDollars, setPriceDollars] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [onHand, setOnHand] = useState('0')

  const categories = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => api<{ items: CategoryOpt[] }>('/admin/categories'),
  })

  const create = useMutation({
    mutationFn: () => {
      const priceCents = centsFromDollars(priceDollars)
      if (!priceCents || priceCents < 1) {
        throw new Error('Le prix doit être supérieur à 0.')
      }
      return apiMutate<ProductDetail>('/admin/products', 'POST', {
        name,
        sku,
        slug: slug || sku.toLowerCase().replace(/\s+/g, '-'),
        description,
        priceCents,
        categoryId,
        onHand: Number.parseInt(onHand, 10) || 0,
        status: 'DRAFT',
      })
    },
    onSuccess: (p) => {
      toast.ok('Produit créé')
      void qc.invalidateQueries({ queryKey: ['admin-products'] })
      window.location.href = `/produits/${p.id}`
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const canSubmit =
    name.trim() &&
    sku.trim() &&
    description.trim() &&
    categoryId &&
    centsFromDollars(priceDollars) > 0 &&
    !create.isPending

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <a href="/produits" className="kicker text-second-admin hover:text-encre-admin">
          ← Produits
        </a>
        <div className="ml-auto flex gap-2">
          <a href="/produits">
            <BtnGhost>Annuler</BtnGhost>
          </a>
          <BtnPrimary
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            Créer
          </BtnPrimary>
        </div>
      </div>

      <section className="carte-admin grid gap-4 p-5 sm:grid-cols-2">
        <Field label="Nom">
          <TextInput
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (!slug) {
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, ''),
                )
              }
            }}
          />
        </Field>
        <Field label="SKU">
          <TextInput value={sku} onChange={(e) => setSku(e.target.value)} />
        </Field>
        <Field label="Slug">
          <TextInput value={slug} onChange={(e) => setSlug(e.target.value)} />
        </Field>
        <Field label="Prix (USD)">
          <TextInput
            type="number"
            step="0.01"
            min="0"
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
          />
        </Field>
        <Field label="Catégorie">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Choisir…</option>
            {(categories.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Stock initial">
          <TextInput
            type="number"
            min="0"
            value={onHand}
            onChange={(e) => setOnHand(e.target.value)}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Description">
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </Field>
        </div>
      </section>
    </div>
  )
}
